/**
 * Live preview of a template as it is being filled in: renders the resolver's blocks (markdown runs with
 * inline value/gap sentinels, plus standalone draft passages) straight to React elements.
 *
 * Deliberately a small bespoke renderer rather than a markdown library: the templates use a fixed,
 * known subset (headings, paragraphs, pipe tables, lists, rules, blockquotes, bold/italic), and building
 * React elements directly means user-entered values can never inject markup — there is no innerHTML
 * anywhere in this file.
 *
 * Provenance is visible by styling: verified template text renders plain; filled values and unfilled
 * gaps are clickable inline chips; LLM-drafted passages sit in a tinted block so they are never mistaken
 * for authored template text.
 */

import { Fragment, type ReactNode } from 'react';
import type { PreviewBlock, PreviewMeta } from '../../lib/templateResolver';


export function TemplatePreview({
  blocks,
  meta,
  focusKey,
  onFocus,
}: {
  blocks: PreviewBlock[];
  meta: PreviewMeta[];
  /** The question key currently focused in the editor column; matching spans highlight. */
  focusKey: string | null;
  /** Called when the user clicks a value/gap/draft in the preview, to focus its question. */
  onFocus: (key: string) => void;
}) {
  return (
    <div className="tpl-paper">
      {blocks.map((block, i) =>
        block.type === 'md' ? (
          <Fragment key={i}>{renderMarkdown(block.text, meta, focusKey, onFocus)}</Fragment>
        ) : (
          <div
            key={i}
            className={`tpl-dblock${block.text ? '' : ' is-empty'}${focusKey === block.key ? ' is-focus' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onFocus(block.key)}
            onKeyDown={(e) => e.key === 'Enter' && onFocus(block.key)}
          >
            <span className="tpl-dblock__chip">
              {block.text ? 'Written with reeve, edit as you like' : `To write: ${block.title}`}
            </span>
            {block.text ? (
              block.text.split(/\n{2,}/).map((para, pi) => (
                <p key={pi}>
                  {para.split('\n').map((line, li) => (
                    <Fragment key={li}>
                      {li > 0 && <br />}
                      {line}
                    </Fragment>
                  ))}
                </p>
              ))
            ) : (
              <p className="tpl-dblock__hint">
                reeve drafts this passage from your answers; you can edit or rewrite it.
              </p>
            )}
          </div>
        ),
      )}
    </div>
  );
}

// ── Block-level markdown ─────────────────────────────────────────────────────────────────────────────

function renderMarkdown(
  text: string,
  meta: PreviewMeta[],
  focusKey: string | null,
  onFocus: (key: string) => void,
): ReactNode[] {
  const inline = (s: string, k?: string | number) => (
    <Fragment key={k}>{renderInline(s, meta, focusKey, onFocus)}</Fragment>
  );

  const out: ReactNode[] = [];
  const lines = text.split('\n');
  let i = 0;
  let key = 0;

  const isTableLine = (l: string) => l.trimStart().startsWith('|');
  const isQuoteLine = (l: string) => l.trimStart().startsWith('>');
  const bulletMatch = (l: string) => /^\s*[-*]\s+(.*)$/.exec(l);
  const orderedMatch = (l: string) => /^\s*\d+\.\s+(.*)$/.exec(l);

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.length === 0) {
      i += 1;
      continue;
    }

    // Horizontal rule.
    if (/^-{3,}$/.test(trimmed)) {
      out.push(<hr key={key++} className="tpl-hr" />);
      i += 1;
      continue;
    }

    // Heading.
    const h = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (h) {
      const level = Math.min(h[1].length, 5);
      const Tag = `h${level}` as 'h1';
      out.push(<Tag key={key++}>{inline(h[2])}</Tag>);
      i += 1;
      continue;
    }

    // Table: consecutive pipe lines; the second row is the alignment separator.
    if (isTableLine(line)) {
      const rows: string[] = [];
      while (i < lines.length && isTableLine(lines[i])) {
        rows.push(lines[i].trim());
        i += 1;
      }
      out.push(renderTable(rows, key++, inline));
      continue;
    }

    // Blockquote.
    if (isQuoteLine(line)) {
      const quoted: string[] = [];
      while (i < lines.length && isQuoteLine(lines[i])) {
        quoted.push(lines[i].trim().replace(/^>\s?/, ''));
        i += 1;
      }
      out.push(
        <blockquote key={key++} className="tpl-quote">
          <p>{inline(quoted.join(' '))}</p>
        </blockquote>,
      );
      continue;
    }

    // Lists.
    if (bulletMatch(line)) {
      const items: string[] = [];
      while (i < lines.length && bulletMatch(lines[i])) {
        items.push(bulletMatch(lines[i])![1]);
        i += 1;
      }
      out.push(<ul key={key++}>{items.map((it, ii) => <li key={ii}>{inline(it)}</li>)}</ul>);
      continue;
    }
    if (orderedMatch(line)) {
      // Numbered legal clauses are often separated by blank lines; keep gathering across them while
      // the next non-blank line is another item, and honour the authored start number so clause 4
      // never renders as "1." (the docx renderer numbers continuously too).
      const start = Number.parseInt(/^\s*(\d+)\./.exec(line)![1], 10) || 1;
      const items: string[] = [];
      while (i < lines.length) {
        if (orderedMatch(lines[i])) {
          items.push(orderedMatch(lines[i])![1]);
          i += 1;
          continue;
        }
        if (lines[i].trim().length === 0) {
          let j = i;
          while (j < lines.length && lines[j].trim().length === 0) j += 1;
          if (j < lines.length && orderedMatch(lines[j])) {
            i = j;
            continue;
          }
        }
        break;
      }
      out.push(
        <ol key={key++} start={start}>
          {items.map((it, ii) => (
            <li key={ii}>{inline(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Paragraph: gather until a blank line or another block form. Single newlines inside a paragraph
    // stay LINE BREAKS (the docx renderer turns every soft break into a real break, and signature and
    // address blocks depend on it), so lines are interleaved with <br /> rather than joined with spaces.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim().length > 0 &&
      !isTableLine(lines[i]) &&
      !isQuoteLine(lines[i]) &&
      !bulletMatch(lines[i]) &&
      !orderedMatch(lines[i]) &&
      !/^(#{1,6})\s/.test(lines[i].trim()) &&
      !/^-{3,}$/.test(lines[i].trim())
    ) {
      para.push(lines[i].trim());
      i += 1;
    }
    out.push(
      <p key={key++}>
        {para.map((pl, pi) => (
          <Fragment key={pi}>
            {pi > 0 && <br />}
            {inline(pl)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return out;
}

function renderTable(
  rows: string[],
  key: number,
  inline: (s: string, k?: string | number) => ReactNode,
): ReactNode {
  const cells = (row: string): string[] => {
    let r = row.trim();
    if (r.startsWith('|')) r = r.slice(1);
    if (r.endsWith('|')) r = r.slice(0, -1);
    return r.split('|').map((c) => c.trim());
  };
  const isSeparator = (row: string) => /^[|\s:-]+$/.test(row) && row.includes('-');

  const header = rows.length > 1 && isSeparator(rows[1]) ? cells(rows[0]) : null;
  const bodyRows = (header ? rows.slice(2) : rows).filter((r) => !isSeparator(r));

  return (
    <div className="tpl-tablewrap" key={key}>
      <table>
        {header && (
          <thead>
            <tr>
              {header.map((c, ci) => (
                <th key={ci}>{inline(c, ci)}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((r, ri) => (
            <tr key={ri}>
              {cells(r).map((c, ci) => (
                <td key={ci}>{inline(c, `${ri}-${ci}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Inline markdown ──────────────────────────────────────────────────────────────────────────────────

/**
 * Inline pass: sentinels (value/gap spans), **bold**, *italic*, `code`, [text](url). Emphasis is
 * toggled rather than nested, which handles bold/italic spanning a sentinel (e.g. **{{NAME}}**).
 */
function renderInline(
  text: string,
  meta: PreviewMeta[],
  focusKey: string | null,
  onFocus: (key: string) => void,
): ReactNode[] {
  const out: ReactNode[] = [];
  let bold = false;
  let italic = false;
  let rest = text;
  let key = 0;

  const pushText = (s: string) => {
    if (s.length === 0) return;
    if (bold || italic) {
      out.push(
        <span key={key++} style={{ fontWeight: bold ? 600 : undefined, fontStyle: italic ? 'italic' : undefined }}>
          {s}
        </span>,
      );
    } else {
      out.push(<Fragment key={key++}>{s}</Fragment>);
    }
  };

  const token = /(\d+)|\*\*|\*|`([^`]*)`|\[([^\]]+)\]\(([^)\s]+)\)|<br\s*\/?>/i;

  while (rest.length > 0) {
    const m = token.exec(rest);
    if (!m) {
      pushText(rest);
      break;
    }
    pushText(rest.slice(0, m.index));
    const matched = m[0];

    if (m[1] !== undefined) {
      const entry = meta[Number.parseInt(m[1], 10)];
      if (entry) {
        const focused = focusKey === entry.key;
        out.push(
          <button
            key={key++}
            type="button"
            className={`tpl-span tpl-span--${entry.kind}${focused ? ' is-focus' : ''}`}
            onClick={() => onFocus(entry.key)}
            title={entry.kind === 'gap' ? 'Click to fill this in' : 'Click to edit'}
          >
            {entry.text.split('\n').map((seg, si) => (
              <Fragment key={si}>
                {si > 0 && <br />}
                {seg}
              </Fragment>
            ))}
          </button>,
        );
      }
    } else if (matched === '**') {
      bold = !bold;
    } else if (matched === '*') {
      italic = !italic;
    } else if (m[2] !== undefined) {
      out.push(
        <code key={key++} className="tpl-code">
          {m[2]}
        </code>,
      );
    } else if (m[3] !== undefined) {
      out.push(
        <a key={key++} href={m[4]} target="_blank" rel="noreferrer">
          {m[3]}
        </a>,
      );
    } else if (matched.toLowerCase().startsWith('<br')) {
      // A few templates carry a literal <br> inside table cells; render a real break, never the tag text.
      out.push(<br key={key++} />);
    }

    rest = rest.slice(m.index + matched.length);
  }

  return out;
}
